import { useEffect, useRef } from 'react'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { getShippingOptionsPromiseDateProps } from 'services/Analytics/orderAnalytics'
import {
  DELIVERY_PROMISE_FETCH_SOURCE,
  getDeliveryPromiseFetchProps,
} from 'services/Analytics/productArrivalsAnalytics'
import { useKeys } from 'services/KeysProvider'
import { getNextHolidayReachableByShipping } from 'services/HolidayService'
import { useAddressStore } from 'stores/RootStoreProvider'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { useCartDeliveryPromisesQuery } from '../../productArrivals/hooks/useCartDeliveryPromisesQuery'
import { getIpLocationAddress } from '../../productArrivals/ipLocationAddress'
import { useDeliveryPromisesQueryInputs } from '../../productArrivals/hooks/useDeliveryPromisesQueryInputs'

interface UseReachableHolidayByProductTypeParams {
  enabled: boolean
}

// With a saved address it shares checkout's queries, also outside holidays, so checkout opens with
// the bag's current dates. Without one it asks by the IP location, which checkout's address form
// starts with too. Those dates are rougher, and checkout refines them once the address is entered.
export function useReachableHolidayByProductType({
  enabled,
}: UseReachableHolidayByProductTypeParams) {
  const { ipCountry, ipState } = useKeys()
  const addressStore = useAddressStore()
  const savedAddress = useMobxSelector(
    () => addressStore.currentAddress,
    [addressStore]
  )
  const deliveryPromisesQueryInputs = useDeliveryPromisesQueryInputs()
  const { deliveryPromiseRequests } = deliveryPromisesQueryInputs
  const deliveryPromiseAddress =
    savedAddress ?? getIpLocationAddress({ ipCountry, ipState })

  const deliveryPromisesQuery = useCartDeliveryPromisesQuery({
    deliveryPromisesQueryInputs: {
      ...deliveryPromisesQueryInputs,
      address: deliveryPromiseAddress,
    },
    enabled,
  })
  const { data: fetchedDeliveryPromises, dataUpdatedAt } = deliveryPromisesQuery

  // Reports only dates the bag asked for while open, so reopening it doesn't report cached ones,
  // and the control, whose bag asks for none, reports nothing here. Removing a product leaves only
  // dates already reported, so a report needs dates newer than the last one.
  const bagOpenedAtRef = useRef(Date.now())
  const lastReportedDataUpdatedAtRef = useRef<number | null>(null)
  useEffect(() => {
    const lastReportedDataUpdatedAt = lastReportedDataUpdatedAtRef.current
    if (
      !enabled ||
      !fetchedDeliveryPromises?.length ||
      dataUpdatedAt < bagOpenedAtRef.current ||
      (lastReportedDataUpdatedAt !== null &&
        dataUpdatedAt <= lastReportedDataUpdatedAt)
    ) {
      return
    }
    lastReportedDataUpdatedAtRef.current = dataUpdatedAt
    analytics.track(EVENT_NAMES.DELIVERY_PROMISE_UPDATED, {
      ...getShippingOptionsPromiseDateProps(fetchedDeliveryPromises),
      ...getDeliveryPromiseFetchProps({
        source: DELIVERY_PROMISE_FETCH_SOURCE.CART,
        hadFullAddress: !!savedAddress,
        state: deliveryPromiseAddress.state,
      }),
    })
  }, [
    dataUpdatedAt,
    deliveryPromiseAddress.state,
    enabled,
    fetchedDeliveryPromises,
    savedAddress,
  ])

  // A changed bag loads with no dates until its changed products arrive, so the lines stay from
  // the last dates and the open drawer doesn't jump.
  const lastDeliveryPromisesRef = useRef(fetchedDeliveryPromises)
  if (fetchedDeliveryPromises) {
    lastDeliveryPromisesRef.current = fetchedDeliveryPromises
  }
  // A failed refresh leaves the last dates in place, but they can no longer be trusted.
  const trustedDeliveryPromises = deliveryPromisesQuery.isError
    ? undefined
    : lastDeliveryPromisesRef.current
  const reachableHolidayByProductType =
    trustedDeliveryPromises &&
    Object.fromEntries(
      deliveryPromiseRequests.map(({ productType }) => [
        productType,
        getNextHolidayReachableByShipping({
          // The last dates have none for a product just added, which gets no line yet.
          shippingOptions: trustedDeliveryPromises.flatMap(
            ({ orderShippingOptions }) =>
              orderShippingOptions[productType] ?? []
          ),
          country: ipCountry,
        }),
      ])
    )

  return {
    status: deliveryPromisesQuery.status,
    data: reachableHolidayByProductType,
  }
}
